/**
 * TermsContent
 * ------------
 * Shared content block for the Terms of Service. Rendered by both the
 * standalone `/terms` route (TermsPage) and the in-place `TermsDialog`
 * opened from the Platform Fee Agreement flow. Single source of truth
 * for the terms copy so we never duplicate the legal text.
 *
 * Accepts a className so callers can tune outer spacing to match the
 * host surface (full page vs. scrollable dialog body).
 */

import React from 'react';

const TermsContent = ({ className = '' }) => {
  return (
    <div className={`prose prose-sm prose-gray max-w-none space-y-6 ${className}`}>
      <section>
        <h2 className="text-lg font-semibold text-gray-900">1. Acceptance of Terms</h2>
        <p className="text-gray-600 leading-relaxed">
          By accessing or using CeleyApp (&ldquo;Celey&rdquo;), operated by The KRS Group LLC, you agree to be bound by these Terms of Service. If you do not agree to these Terms, please do not use Celey.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">2. Platform Overview</h2>
        <p className="text-gray-600 leading-relaxed">
          CeleyApp is an event planning platform that helps customers discover, communicate with, book, and pay event service providers (&ldquo;Vendors&rdquo;).
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">3. User Accounts</h2>
        <p className="text-gray-600 leading-relaxed">
          You must provide accurate, complete information when creating an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">4. Vendor Participation</h2>
        <p className="text-gray-600 leading-relaxed">
          Vendors agree to provide services as described in their profiles. By listing services on Celey, vendors authorize CeleyApp to facilitate payments through Stripe Connect. Vendors acknowledge and agree to the following:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
          <li>A <strong>3% platform fee</strong> is charged on all completed bookings</li>
          <li>Payment processing is handled by Stripe, subject to Stripe&apos;s terms</li>
          <li>Vendors are independent contractors, not employees of CeleyApp</li>
          <li>Vendors are responsible for the quality of their services</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">5. Customer Responsibilities</h2>
        <p className="text-gray-600 leading-relaxed">
          Customers agree to provide accurate event details, make timely payments, and communicate respectfully with vendors. The cancellation and refund policy that applies to every booking is described in Section 6 below.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">6. Payments, Fees &amp; Cancellation Policy</h2>
        <p className="text-gray-600 leading-relaxed">
          All payments are processed securely through Stripe. CeleyApp does not store credit card information. A 15% deposit is charged at the time the Service Provider accepts the booking; the remaining 85% is collected closer to the event date and released to the Service Provider after the job is completed or after the auto-release window expires.
        </p>
        <p className="text-gray-600 leading-relaxed mt-3">
          The following cancellation and refund rules apply to every booking on Celey:
        </p>
        <ul className="list-disc list-inside text-gray-600 space-y-2 mt-2">
          <li><strong>Customer cancellation within 48 hours of paying the deposit:</strong> full refund of the deposit.</li>
          <li><strong>Customer cancellation after 48 hours:</strong> the deposit is non-refundable. Any final payment already collected at the time of cancellation will be refunded; only the deposit is non-refundable after 48 hours.</li>
          <li><strong>Service Provider cancellation more than 72 hours before the event:</strong> the Customer receives a full refund of all amounts paid.</li>
          <li><strong>Service Provider cancellation within 72 hours of the event:</strong> not permitted for paid, secured bookings. Service Providers may, however, close out an unpaid booking whose deposit window has expired at any time — including within the 72-hour window — because no funds were ever collected from the Customer. A close-out of this kind is not the same as cancelling a paid booking.</li>
          <li><strong>Refund timing:</strong> approved refunds are issued through the original payment method via Stripe and typically appear within 5&ndash;10 business days.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">7. Dispute Resolution</h2>
        <p className="text-gray-600 leading-relaxed">
          In the event of a dispute between a customer and vendor, CeleyApp may facilitate resolution but is not obligated to arbitrate. Users agree to first attempt to resolve disputes directly before involving CeleyApp support.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">8. Limitation of Liability</h2>
        <p className="text-gray-600 leading-relaxed">
          CeleyApp is a platform for connecting users and is not responsible for the actions, quality of service, or conduct of any vendor or customer. The Platform is provided &quot;as is&quot; without warranties of any kind.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">9. Privacy</h2>
        <p className="text-gray-600 leading-relaxed">
          Your use of the Platform is also governed by our Privacy Policy. We collect and process personal data as necessary to operate the Platform and facilitate transactions between users.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">10. Changes to Terms</h2>
        <p className="text-gray-600 leading-relaxed">
          We reserve the right to modify these Terms at any time. Changes will be posted on this page with an updated effective date. Continued use of the Platform after changes constitutes acceptance.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">11. Contact</h2>
        <p className="text-gray-600 leading-relaxed">
          For questions about these Terms, contact us through the CeleyApp support system or at the contact information provided within Celey.
        </p>
      </section>
    </div>
  );
};

export default TermsContent;
